import React from 'react';
import PropTypes from 'prop-types';

/**
 * CardDisplay component shows the name, orientation, and description of the
 * selected Tarot card. If no card is provided, a placeholder is
 * rendered instead.
 */
export default function CardDisplay({ card }) {
  if (!card) {
    return (
      <div className="card-altar min-h-[200px] flex flex-col items-center justify-center text-center">
        <div className="text-6xl mb-4 opacity-30">🂠</div>
        <p className="body-text opacity-60">Awaiting Your Selection</p>
        <div className="mt-2 text-xs status-mystical">
          The cards rest in sacred silence
        </div>
      </div>
    );
  }

  // Format orientation for display
  const orientationDisplay = card.orientation === 'upright' 
    ? '' 
    : card.orientation === 'reversed'
    ? ' (Drawn Reversed)'
    : ' (Drawn Sideways)';
  
  const orientationSymbol = card.orientation === 'upright'
    ? '🂡'
    : card.orientation === 'reversed'
    ? '🂮'
    : '🂱';

  // Get the appropriate meaning based on orientation
  const meaning = card.orientation === 'reversed'
    ? card.reversed_meaning
    : card.upright_meaning;

  // Get suit emoji for Minor Arcana cards
  const getSuitEmoji = () => {
    if (card.arcana !== 'Minor' || !card.suit) return '';
    const suitEmojis = {
      'Swords': '⚔️',
      'Wands': '🪄',
      'Cups': '🏆',
      'Pentacles': '✡️'
    };
    return suitEmojis[card.suit] || '';
  };
  
  const suitEmoji = getSuitEmoji();

  return (
    <div className="card-altar card-glow min-h-[200px] flex flex-col items-center justify-center text-center">
      <div className="text-4xl mb-4">{orientationSymbol}</div>
      <h3 className="heading-secondary text-lg mb-1">
        {suitEmoji && <span className="mr-2">{suitEmoji}</span>}
        {card.name}
      </h3>
      {orientationDisplay && (
        <p className="text-xs text-amber-400 italic mb-3">{orientationDisplay}</p>
      )}
      
      {/* Card Meaning based on orientation - longer interpretation shown below */}
      {meaning && (
        <p className="body-text text-sm opacity-80 leading-relaxed">
          {meaning}
        </p>
      )}
      
      <div className="mt-3 text-xs status-mystical">
        Energy Aligned ✦ Ready for Synthesis
      </div>
    </div>
  );
}

CardDisplay.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string.isRequired,
    orientation: PropTypes.oneOf(['upright', 'reversed', 'sideways']),
    arcana: PropTypes.string,
    suit: PropTypes.string,
    upright_meaning: PropTypes.string,
    reversed_meaning: PropTypes.string,
  }),
};

CardDisplay.defaultProps = {
  card: null,
};
